// M4 workstream B (markets & money): shared state, gating and small helpers. Everything here is inert before
// 1 Jan 2000 (rules.M4_FROM): no state is created, no RNG is drawn and no money moves, so M1-M3 games are byte-identical.
// State lives in state.m4markets (lazy, JSON-safe). Randomness uses derived streams (rng.subRng keyed by seed + day),
// so M4 market draws never shift the main state.rng sequence other workstreams rely on, and a reloaded save replays.
import { M4_FROM } from '../../data/m4/rules.js';
import { REGIONS } from '../../data/regions.js';
import { nextFloat, subRng } from '../../core/rng.js';
import { yf, fieldDef } from '../defs.js';
import { wellsOnField } from '../indexes.js';
import { fieldMaxWater } from '../../data/m3/fields.js';

export { M4_FROM };
export const m4On = (state) => yf(state) >= M4_FROM;

export function mk(state) {
  const M = (state.m4markets ??= {});
  M.zoneKbd ??= {};          // player crude output by takeaway zone today (kbd), for the endogenous spread
  M.shutIns ??= {};          // wellId -> day shut in (restart cost & shale damage on restart)
  M.curtail ??= {};          // fieldId -> { carry, share, reason } (fractional curtailment by rotating wells)
  M.firm ??= {};             // fieldId -> take-or-pay firm pipeline commitment
  M.gas ??= {};              // fieldId -> { outlet, mode, capEma, prodEma, ... } gas marketing state
  M.lng ??= {};              // fieldId -> LNG tolling contract
  M.spa ??= {};              // fieldId -> LNG SPA
  M.cushing ??= [];          // Cushing contango leases
  M.hedges ??= [];           // M4 swaps / collars / puts / three-ways
  M.nextId ??= 1;
  M.wti ??= { key: null, sum: 0, n: 0, lastAvg: null, lastKey: null }; // calendar-month WTI average (swap settlement)
  M.esg ??= { year: null, gasMcf: 0, flaredMcf: 0, routineFlaredMcf: 0, methaneT: 0, co2T: 0, repDebt: 0, prevYear: null };
  M.ledger ??= { gasSales: 0, nglSales: 0, flarePenalty: 0, carbon: 0, credits45q: 0, carbonTax: 0, storageRent: 0, takeOrPay: 0, contango: 0 };
  M.reactivating ??= {};     // rigUid -> { untilDay, cost }
  return M;
}
export const peek = (state) => state.m4markets ?? null;
export const nid = (state, p) => `${p}${mk(state).nextId++}`;

// Deterministic per-day draw on a named stream (does not touch state.rng).
export function rand(state, key) {
  const seed = ((state.seed ?? 0) ^ Math.imul((state.day ?? 0) + 1, 2654435761)) >>> 0;
  return nextFloat(subRng(seed, `m4b:${key}`));
}

// M4 profit & loss lines. They are created lazily in the month record (finance.newMonth is untouched, so pre-2000
// states keep their exact shape); finance.monthNet adds them through the marked M4-B hunk.
export const M4_PL_CATS = ['gasSales', 'storage', 'carbon', 'carbonTax', 'credits45q'];
export const M4_PL_INCOME = new Set(['gasSales', 'credits45q']);

// US regions added by M4 (not in the M3 US_PRICE_REGIONS list, M4_SPEC §18.11).
export const M4_US_REGIONS = new Set(['north_dakota', 'colorado', 'louisiana']);
export const isUsRegionId = (rid) => REGIONS[rid]?.country === 'United States' || M4_US_REGIONS.has(rid);

// Player wells on a field that are producing today (rate > 0).
export function producingOn(state, fieldId) {
  return wellsOnField(state, fieldId).filter((w) => w.status === 'PRODUCING' && w.prod && !w.ownerOperatorId && w.prod.rate > 0);
}
export const isOffshoreField = (fieldId) => { const f = fieldDef(fieldId); return !!f && fieldMaxWater(f) > 20; };
export const r2 = (x) => Math.round(x * 100) / 100;
export const clamp01 = (x) => Math.max(0, Math.min(1, x));

// Reservoir definition by id (reservoirs live under field.prospects[].reservoirs).
const RES_CACHE = new Map();
export function resDefOf(fieldId, resId) {
  const k = `${fieldId}|${resId}`;
  if (RES_CACHE.has(k)) return RES_CACHE.get(k);
  let r = null;
  for (const p of fieldDef(fieldId)?.prospects ?? []) { r = (p.reservoirs ?? []).find((x) => x.id === resId) ?? null; if (r) break; }
  if (fieldDef(fieldId)) RES_CACHE.set(k, r);
  return r;
}
